import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import {
  Badge,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Tooltip,
  Stack,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { FormLayoutBuilder, getFormData, DataTable } from '@pihealth/common-ui-library';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Notes from 'src/components/common/Notes';
import { useFieldTranslation } from 'src/components/fields/useFieldTranslation';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUser } from 'src/components/hooks/useUser';
import AppModal from 'src/components/modal';
import { archLevel } from 'src/data/config';
import { setMessage } from 'src/store/Message';
import { useAppSelector } from 'src/store/hooks';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import forms from '../../../../resources/forms.json';
import DARDocUpload from '../../documentUpload/DARDocUpload';
import './DrugAccountability.scss';
import KitID from './KitID';
import { useDrugAccountability } from './useDrugAccountability';
import { GridToolbarExport, GridValueGetterParams } from '@mui/x-data-grid-pro';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { useGetPharmacyListMutation } from 'src/services/workarea';

const DrugAccountability: React.FunctionComponent<any> = ({ setDragAccount, setStudyLogOpen }) => {
  const handleBackToStudylog = () => {
    setDragAccount(false);
    setStudyLogOpen(true);
  };
  const formGroup = forms.DARAddKit;
  const editformGroup = forms.DAREditKit;
  const [codes, setCodes] = useState(useAppSelector(state => state.codelistData.codeListObjData));
  const { formFields, defaultValues = {}, rules } = getFormData(formGroup.groups, codes);
  const { formFields: editformField, rules: editRules } = getFormData(editformGroup.groups, codes);
  const { groups } = formFields;
  const { groups: editGroups } = editformField;
  const formRef: any = useRef(null);
  const dispatch = useDispatch();
  const [isGridDataLoading, setIsGridDataLoading] = useState(false);
  const { checkPermission } = usePermissionWrapper();
  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const [progressDocUpload, setProgressDocUpload] = useState<boolean>(false);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const currentAppName = archLevel == 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_TSDA_SLUG;
  const [rowsCount, setRowsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(0);
  const [filterObject, setFilterObject] = useState<any>();
  const { user } = useUser();
  const [updateEditData, setUpdateEditData] = useState<any>({});
  const [onEdit, setOnEdit] = useState(false);
  const [openConfirmExit, setOpenConfirmExit] = useState(false);
  const [isValue, setIsValue] = useState(false);
  const { duFileUploadErrorMsg, duDocumentUploadSuccessMsg } = useFieldTranslation();
  const { userPreferedDateFormat } = usePreferedDateFormat();
  const {
    createDrugAccRecord,
    getDrugAccountRecord,
    getDrugAccountRecordData,
    getDrugAccountRecordError,
    getDrugAccountRecordLoading,
    updateDrugKitRecords,
    updateDrugKitDoc,
    drugKitCreateNotes,
    getDrugKitAllNotes,
    getIpData
  } = useDrugAccountability();
  const [notesData, setNotesData] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState<any>([]);
  const [singleKitData, setSingleKitData] = useState({});
  const [kitPageEnable, setKitPageEnable] = useState<boolean>(true);
  const [pageSize, setPageSize] = React.useState(10);
  const [enable, setEnable] = useState<boolean>(false);
  const handleAddKitPopUp = () => {
    setOnEdit(false);
    setEnable(true);
    setIsValue(false);
  };
  const [getPharmacyList, { data: pharmacyListData }] = useGetPharmacyListMutation();
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (pharmacyListData) {
      const tempArr =
        pharmacyListData.length > 0
          ? pharmacyListData.map((ele: any) => {
              return {
                displayName: ele.pharmacy_name,
                key: ele.pharmacy_name
              };
            })
          : [];
      setCodes((prev: any) => ({ ...prev, PHRMLIST: tempArr }));
    }
  }, [pharmacyListData]);
  const handlechange = (key: string, value: string) => {
    if (key && value) {
      setIsValue(true);
    } else {
      setIsValue(false);
    }
    return value;
  };
  const handleCancelAddKit = () => {
    if (isValue) {
      setOpenConfirmExit(true);
    } else {
      setEnable(false);
      setOpenConfirmExit(false);
    }
  };
  const handleSubmit = () => {
    formRef.current.submit(onFormSubmit);
  };
  const onFormSubmit = (data: any) => {
    if (data.errors) {
      return;
    }
    if (data.success) {
      if (onEdit) {
        handleEditKit({
          ...data.records,
          da_log_expiration_date: moment(data.records.da_log_expiration_date).utcOffset(0, true).format()
        });
      } else {
        handleSaveAddKit({
          ...data.records,
          da_log_expiration_date: moment(data.records.da_log_expiration_date).utcOffset(0, true).format()
        });
      }
    }
    setEnable(false);
  };
  const handleSaveAddKit = async (data: any) => {
    await createDrugAccRecord({
      site_id: selectedSite,
      study_id: studyId,
      data: { ...data, custom_name: selectedIpName, created_by: user },
      country: selectedCountry,
      application: process.env.REACT_APP_NAME,
      module_section_related: 'STUDY LOGS',
      parent_entity_related: ''
    });
    setEnable(false);
    getAllDrugAccountabilityKits(pageNumber * pageSize, pageSize);
  };
  const handleKitIdClick = (row: any) => {
    setSingleKitData(row);
    setKitPageEnable(false);
  };
  const [commentDilogOpen, setCommentDilogOpen] = useState<boolean>(false);
  const [notetext, setNoteText] = useState('');
  const [id, setID] = useState('');
  const handleCommentOpen = (row: any) => {
    setNotesData([]);
    setID(row.id);
    getNotesData(row.id);
    setCommentDilogOpen(true);
  };
  const handleSendNote = () => {
    if (notetext !== '') {
      sendNotesData(id);
      setNoteText('');
    }
  };
  const sendNotesData = async (id: string) => {
    try {
      await drugKitCreateNotes({
        kit_id: id,
        data: {
          da_log_kit_notes: notetext,
          created_by: user
        },
        country: selectedCountry,
        application: process.env.REACT_APP_NAME,
        site_id: selectedSite
      });
    } catch (error: any) {
      if (error && error?.response?.status === 403) {
        dispatch(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
      } else if (error && error.response?.status === 401) {
        dispatch(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
      }
    }
    await getNotesData(id);
  };
  const getNotesData = async (id: any) => {
    try {
      const result: any = await getDrugKitAllNotes({
        kit_id: id,
        country: selectedCountry,
        application: process.env.REACT_APP_NAME,
        site_id: selectedSite
      });
      setNotesData(result?.data);
    } catch (error: any) {
      if (error && error?.response?.status === 403) {
        dispatch(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
      } else if (error && error.response?.status === 401) {
        dispatch(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
      }
      setNotesData([]);
    }
  };
  const [rows, setrows] = useState([]);
  const [ipList, setIpList] = useState<any>([]);
  const [selectedIpName, setSelectedIPName] = useState('');
  const slectedIpInitialValues = {
    amount_in_kit: '',
    custom_name: '',
    dose: '',
    drug_formulation: '',
    investigational_drug: '',
    storage_temperature: '',
    unit: ''
  };
  const [selectedIpdata, setSelectedIpData] = useState(slectedIpInitialValues);

  useEffect(() => {
    if (getDrugAccountRecordData) {
      setrows([]);
      setRowsCount(getDrugAccountRecordData.record_count);
      const temp = getDrugAccountRecordData.selected_data.map((item: any) => {
        return {
          ...item.kit_data,
          ...item,
          docCount: item.file_ids.length,
          id: item.kit_data.id
        };
      });
      setrows(temp);
    }
    setIsGridDataLoading(false);
  }, [getDrugAccountRecordData]);

  useEffect(() => {
    if (getDrugAccountRecordError) {
      setRefreshKey(prev => prev + 1);
      setrows([]);
      setRowsCount(0);
      setIsGridDataLoading(false);
    }
  }, [getDrugAccountRecordError]);

  const getAllDrugAccountabilityKits = async (from: number, size: number) => {
    setIsGridDataLoading(true);
    await getDrugAccountRecord({
      ip_name: selectedIpName,
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      data: filterObject,
      application: currentAppName,
      module_section_related: 'DrugAccountablityLog',
      parent_entity_related: '',
      from,
      size
    });
  };
  useEffect(() => {
    if (!studyId) {
      setIpList([{}]);
      setSelectedIPName('');
      setSelectedIpData(slectedIpInitialValues);
    }
    if (selectedSite) {
      getPharmacyList({
        study_id: studyId,
        site_id: selectedSite,
        application: archLevel === 'sponsor' ? process.env.REACT_APP_NAME : process.env.REACT_APP_NAME_TEMP
      });
    }
    selectedIpName && getAllDrugAccountabilityKits(0, 10);
  }, [selectedSite, selectedCountry, studyId]);
  useEffect(() => {
    getAllDrugAccountabilityKits(0, 10);
  }, [selectedIpName]);
  useEffect(() => {
    if (!getDrugAccountRecordLoading && (pageSize === 10 || pageSize === 20 || pageSize === 30)) {
      if (pageNumber * pageSize > rowsCount - 1) {
        setPageNumber(0);
        getAllDrugAccountabilityKits(0, pageSize);
      } else {
        getAllDrugAccountabilityKits(pageNumber * pageSize, pageSize);
      }
    }
  }, [pageNumber, pageSize]);

  useEffect(() => {
    if (filterObject) {
      getAllDrugAccountabilityKits(0, pageSize);
    }
  }, [filterObject]);

  useEffect(() => {
    if (getIpData) {
      setIpList(getIpData);
      setSelectedIPName(getIpData[0]?.custom_name);
      setSelectedIpData(getIpData[0]);
    }
  }, [getIpData]);

  const [uploadDilogOpen, setUploadDilogOpen] = useState<boolean>(false);
  const documentMetaData = {
    name: 'Drug accountability log',
    number: '10.03.06'
  };
  const handleFileUploadOpen = (row: any) => {
    setID(row.id);
    setUploadedFiles([...row.file_ids]);
    setUploadDilogOpen(true);
  };
  const updateDocs = async (updatedDocIds: string[], isDocDelete = false) => {
    setProgressDocUpload(true);
    try {
      await updateDrugKitDoc({
        kit_id: id,
        data: {
          uploaded_file_ids: updatedDocIds
        },
        country: selectedCountry,
        application: process.env.REACT_APP_NAME
      });
      setUploadedFiles(updatedDocIds);
      if (isDocDelete) {
        dispatch(setMessage({ msgDetails: 'Document Deleted successfuly', severity: 'success' }));
      } else {
        dispatch(setMessage({ msgDetails: duDocumentUploadSuccessMsg, severity: 'success' }));
      }
    } catch (error: any) {
      if (error && error?.response?.status === 403) {
        dispatch(setMessage({ msgDetails: 'Error 403 Restricted User permission', severity: 'error' }));
      } else if (error && error.response?.status === 401) {
        dispatch(setMessage({ msgDetails: 'Error 401 Unauthorized access', severity: 'error' }));
      } else if (isDocDelete) {
        dispatch(setMessage({ msgDetails: 'Error in Document Delete', severity: 'success' }));
      } else {
        dispatch(setMessage({ msgDetails: duFileUploadErrorMsg, severity: 'error' }));
      }
    }
    setProgressDocUpload(false);
    setUploadDilogOpen(false);
    getAllDrugAccountabilityKits(pageNumber * pageSize, pageSize);
    setID('');
  };
  const handleEdit = (row_data: any) => {
    setID('');
    setUpdateEditData({ ...row_data.kit_data, updated_by: user });
    setID(row_data.id);
    setOnEdit(true);
    setEnable(true);
    setIsValue(false);
  };

  const formatDate = (params: any) => {
    return moment(params.row.da_log_expiration_date).format(`${userPreferedDateFormat.toUpperCase()}`);
  };
  const columns = [
    {
      field: 'da_log_kit_id',
      headerName: 'Kit ID',
      minWidth: 100,
      flex: 1,
      renderCell: (params: any) =>
        checkPermission('view_kit_details') ? (
          <>
          <Tooltip title={params?.value.length > 10 ? params?.value : null} >
            <Link style={{ cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => handleKitIdClick(params.row)}>
              {params.value?.toString()}
            </Link>
          </Tooltip>
          </>
        ) : (
          <>{params.value?.toString()}</>
        )
    },
    {
      field: 'da_log_pharmacy',
      headerName: 'Pharmacy',
      flex: 1
    },
    {
      field: 'da_log_location',
      headerName: 'Storage Location',
      flex: 1
    },
    {
      field: 'da_log_nickname',
      headerName: 'Nickname',
      flex: 1
    },
    {
      field: 'amount_in_kit',
      headerName: 'Amount in Kit',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.da_log_amount_in_kit || ''} ${params.row.da_log_amount_in_kit_unit || ''}`
    },
    {
      field: 'vial_size',
      headerName: 'Vial Size',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.da_log_vial_size || ''} ${params.row.da_log_vial_size_unit || ''}`
    },
    {
      field: 'expiration_date',
      headerName: 'Expiration Date',
      flex: 1,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'da_log_balance_forward_match',
      headerName: 'Balance Forward',
      minWidth: 100,
      flex: 1,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.da_log_balance_forward || ''}/${params.row.da_log_amount_in_kit || ''}`
    },
    {
      field: 'da_log_temperature_excursion',
      headerName: 'Temperature Excursion',
      flex: 1
    },
    {
      field: 'da_log_lot_num',
      headerName: 'Lot No.',
      flex: 1
    },
    {
      field: 'action',
      headerName: 'Action',
      minWidth: checkPermission('add_new_kit') ? 120 : 84,
      flex: 1,
      disableExport: true,
      filterable: false,
      renderCell: (params: any) => (
        <>
          <Stack direction="row" spacing={0} sx={{ justifyContent: 'space-between' }}>
            <IconButton onClick={() => handleFileUploadOpen(params.row)} title="Attach File">
              <AttachFileIcon color="primary" />
              <Badge
                badgeContent={params.row.docCount}
                color="primary"
                className="smallBadgeSearch"
                sx={{ mt: -2.5 }}
              />
            </IconButton>
            <IconButton onClick={() => handleCommentOpen(params.row)} title="Comment">
              <CommentIcon sx={{ color: '#2196F3' }} />
            </IconButton>
            {checkPermission('add_new_kit') ? (
              <IconButton onClick={() => handleEdit(params.row)} title="Edit">
                <EditIcon />
              </IconButton>
            ) : null}
          </Stack>
        </>
      )
    }
  ];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleEditKit = async (data: any) => {
    if (
      updateEditData.da_log_kit_id === data.da_log_kit_id &&
      updateEditData.da_log_location === data.da_log_location &&
      updateEditData.da_log_lot_num === data.da_log_lot_num &&
      updateEditData.da_log_amount_in_kit === data.da_log_amount_in_kit &&
      updateEditData.da_log_amount_in_kit_unit === data.da_log_amount_in_kit_unit &&
      updateEditData.da_log_vial_size === data.da_log_vial_size &&
      updateEditData.da_log_vial_size_unit === data.da_log_vial_size_unit &&
      updateEditData.da_log_expiration_date === data.da_log_expiration_date &&
      updateEditData.da_log_balance_forward === data.da_log_balance_forward &&
      updateEditData.da_log_temperature_excursion === data.da_log_temperature_excursion &&
      updateEditData.da_log_pharmacy === data.da_log_pharmacy &&
      updateEditData.da_log_nickname === data.da_log_nickname
    ) {
      dispatch(setMessage({ msgDetails: 'No change in data and hence cannot save the information', severity: 'info' }));
      setUpdateEditData({});
      setEnable(false);
      setOnEdit(false);
    } else {
      await updateDrugKitRecords({
        site_id: selectedSite,
        study_id: studyId,
        id,
        data,
        country: selectedCountry,
        application: process.env.REACT_APP_NAME
      });
      setUpdateEditData({});
      setEnable(false);
      getAllDrugAccountabilityKits(pageNumber * pageSize, pageSize);
      setOnEdit(false);
    }
  };
  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `Drug Accountability Record ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };
  const dataTableProps = {
    filterMode: 'server'
  };
  const filterValues = (data: any) => {
    setFilterObject(data);
  };
  const ipHandleCheck = (e: any) => {
    const data = e?.target?.value;
    const findIpdetails = ipList.find((item: any) => item.custom_name === data);

    setSelectedIPName(data);
    setSelectedIpData(findIpdetails);
  };
  return (
    <>
      {kitPageEnable ? (
        <>
          <Box mt={-1}>
            <Button onClick={handleBackToStudylog} startIcon={<ArrowBackIcon fontSize="small" />}>
              LIST OF LOGS
            </Button>
            / Drug Accountability Record
          </Box>
          <Box>
            <Typography variant="h1" sx={{ fontSize: '1.5rem', fontWeight: 'bold' }} mb={2} mt={2}>
              Drug Accountability Record - Kit Level
            </Typography>
            <Grid container spacing={2} mb={2}>
              <Grid item md={4}>
                <InputLabel id="invest_prod-label" required>
                  Select IP
                </InputLabel>
                <FormControl variant="standard" fullWidth>
                  <Select
                    labelId="invest_prod-label"
                    id="custom_name"
                    defaultValue={selectedIpName}
                    value={selectedIpName}
                    required
                    onChange={e => ipHandleCheck(e)}
                    name="custom_name"
                    sx={{ minWidth: '300px' }}
                  >
                    {ipList?.map((value: any) => {
                      return (
                        <MenuItem key={value?.custom_name} value={value?.custom_name}>
                          <Tooltip placement="top" arrow title={value?.custom_name}>
                            <span>
                              {value?.custom_name?.length > 30 ? (
                                <div
                                  style={{
                                    maxWidth: '300px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {value?.custom_name}
                                </div>
                              ) : (
                                value?.custom_name
                              )}
                            </span>
                          </Tooltip>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} mt={2} mb={2}>
              <Grid item xs={4}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  Investigational Drug:
                  <span className="results"> {selectedIpdata?.investigational_drug}</span>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  Drug Formulation:
                  <span className="results"> {selectedIpdata?.drug_formulation}</span>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  Amount in kit:
                  <span className="results"> {selectedIpdata?.amount_in_kit} </span>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  Storage Temperature:
                  <span className="results"> {selectedIpdata?.storage_temperature}</span>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  Dose:
                  <span className="results"> {selectedIpdata?.dose}</span>
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                  Dose Unit:
                  <span className="results"> {selectedIpdata?.unit}</span>
                </Typography>
              </Grid>
            </Grid>
            <Grid md={12} sx={{ position: 'relative' }}>
              {checkPermission('add_new_kit') ? (
                <Box mb={3} display={'flex'} justifyContent={'flex-end'}>
                  <Button variant="contained" onClick={handleAddKitPopUp} disabled={selectedIpName === ''}>
                    ADD KIT
                  </Button>
                </Box>
              ) : (
                ''
              )}
            </Grid>
            <Box sx={{ clear: 'both' }}> </Box>

            <DataTable
              key={`Dar-${refreshKey}`}
              height="66vh"
              paging
              pagination
              hasDefinedColumns
              hasDefinedDataGridToolbarExport
              definedGridToolbarExport={definedCustomDatGridExport}
              columns={columns}
              pageSize={pageSize}
              setPageSize={setPageSize}
              rows={rows}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                  textOverflow: 'clip',
                  whiteSpace: 'normal !important',
                  wordWrap: 'break-word !important',
                  lineHeight: 1.3
                }
              }}
              loading={isGridDataLoading || getDrugAccountRecordLoading}
              exportCSVName={`Drug Accountability Record`}
              datePreference={userPreferedDateFormat}
              rowsCount={rowsCount}
              page={pageNumber}
              setPageNumber={setPageNumber}
              paginationMode="server"
              dataTableProps={dataTableProps}
              filterValues={(data: any) => filterValues(data)}
            />

            <AppModal
              open={enable}
              title={onEdit ? editformGroup.header : formGroup.header}
              handleSubmit={handleSubmit}
              handleClose={handleCancelAddKit}
            >
              <FormLayoutBuilder
                isDisableRule
                initialValues={onEdit ? updateEditData : defaultValues}
                rules={onEdit ? editRules : rules}
                ref={formRef}
                groups={onEdit ? editGroups : groups}
                columnSpacing={2}
                onChange={(key: any, value: any) => handlechange(key, value)}
              />
            </AppModal>
            <Notes
              commentDilogOpen={commentDilogOpen}
              setCommentDilogOpen={setCommentDilogOpen}
              notesData={notesData}
              handleSendNote={handleSendNote}
              notetext={notetext}
              setNoteText={setNoteText}
              permission={checkPermission('update_kit_return_event')}
            />
            <DARDocUpload
              documentMetaData={documentMetaData}
              uploadDilogOpen={uploadDilogOpen}
              setUploadDilogOpen={setUploadDilogOpen}
              uploadedFiles={uploadedFiles}
              updateDocs={updateDocs}
              progress={progressDocUpload}
              permission={checkPermission('update_kit_return_event')}
            />
          </Box>
        </>
      ) : (
        <KitID setKitPageEnable={setKitPageEnable} singleKitData={singleKitData} />
      )}
      <PopupComponent
        open={openConfirmExit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="confirm-modal"
        child
      >
        <DialogTitle id="alert-dialog-title">
          <b>Confirm Exit</b>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Changes you made will not be saved. Are you sure you want to exit before saving your changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirmExit(false);
            }}
            className="grey-btn"
          >
            CLOSE
          </Button>
          <Button
            onClick={() => {
              setOpenConfirmExit(false);
              setEnable(false);
            }}
          >
            EXIT
          </Button>
        </DialogActions>
      </PopupComponent>
    </>
  );
};
export default DrugAccountability;
